package com.isaias.fyattravel.web.auth

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.isaias.fyattravel.web.i18n.WebStringKey
import com.isaias.fyattravel.web.i18n.WebStrings
import com.isaias.fyattravel.web.theme.ThemeColors
import org.w3c.dom.HTMLElement

/**
 * Web Authentication Page
 * Uses Firebase Auth UI for Google, Email, and Phone authentication
 */
@Composable
fun WebAuthPage(
    onAuthSuccess: () -> Unit = { window.location.hash = "#/" }
) {
    val authState = rememberAuthState()
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var isInitialized by remember { mutableStateOf(false) }
    
    // Check if already logged in
    LaunchedEffect(authState.isAuthenticated) {
        if (authState.isAuthenticated && !authState.isLoading) {
            onAuthSuccess()
        }
    }
    
    // Initialize Firebase UI
    LaunchedEffect(Unit) {
        // Give Firebase scripts time to load
        window.setTimeout({
            AuthManager.initialize()
            isInitialized = true
        }, 500)
    }
    
    // Start Firebase UI when container is ready
    LaunchedEffect(isInitialized) {
        if (isInitialized && !authState.isAuthenticated) {
            window.setTimeout({
                try {
                    AuthManager.startAuthUI(
                        containerId = "firebaseui-auth-container",
                        onSuccess = {
                            console.log("Authentication successful")
                            onAuthSuccess()
                        },
                        onError = { error ->
                            errorMessage = error
                        }
                    )
                } catch (e: dynamic) {
                    errorMessage = "Failed to initialize authentication. Please refresh the page."
                    console.error("Auth UI error:", e)
                }
            }, 100)
        }
    }
    
    Div({
        style {
            maxWidth(480.px)
            property("margin", "0 auto")
            padding(2.em)
        }
    }) {
        // Header
        Div({
            style {
                textAlign("center")
                marginBottom(2.em)
            }
        }) {
            // Logo
            Div({
                style {
                    width(64.px)
                    height(64.px)
                    borderRadius(16.px)
                    backgroundColor(ThemeColors.primary)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    color(ThemeColors.textOnDark)
                    fontWeight("800")
                    fontSize(1.5.em)
                    property("margin", "0 auto")
                    marginBottom(1.em)
                }
            }) {
                Text("F")
            }
            
            H1({
                style {
                    fontSize(1.75.em)
                    fontWeight("800")
                    color(ThemeColors.textPrimary)
                    marginBottom(0.5.em)
                }
            }) { Text("Welcome Back") }
            
            P({
                style {
                    color(ThemeColors.textMuted)
                    fontSize(1.em)
                }
            }) { Text("Sign in to access your account, save favorites, and book travel packages.") }
        }
        
        // Error Message with Retry
        errorMessage?.let { error ->
            Div({
                style {
                    backgroundColor(ThemeColors.errorBg)
                    color(ThemeColors.error)
                    padding(1.em)
                    borderRadius(0.75.em)
                    marginBottom(1.em)
                    fontWeight("600")
                }
            }) {
                Text(error)
            }
            // Retry button
            Button({
                style {
                    width(100.percent)
                    backgroundColor(ThemeColors.primary)
                    color(ThemeColors.textOnGold)
                    padding(0.75.em)
                    borderRadius(0.5.em)
                    border(0.px)
                    cursor("pointer")
                    fontWeight("600")
                    fontSize(1.em)
                    marginBottom(1.em)
                }
                onClick {
                    errorMessage = null
                    isInitialized = false
                    window.setTimeout({
                        AuthManager.initialize()
                        isInitialized = true
                    }, 500)
                }
            }) {
                Text("Retry")
            }
        }
        
        // Loading State
        if (authState.isLoading) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    padding(2.em)
                }
            }) {
                Div({
                    style {
                        width(40.px)
                        height(40.px)
                        borderRadius(50.percent)
                        property("border", "4px solid ${ThemeColors.divider}")
                        property("border-top-color", "${ThemeColors.primary}")
                        property("animation", "spin 1s linear infinite")
                    }
                }) {}
            }
        }
        
        // Firebase Auth UI Container
        if (!authState.isLoading && !authState.isAuthenticated) {
            Div({
                style {
                    backgroundColor(ThemeColors.surface)
                    padding(1.5.em)
                    borderRadius(1.em)
                    property("box-shadow", "0 4px 12px rgba(0, 0, 0, 0.08)")
                }
            }) {
                // This div will contain Firebase Auth UI
                Div(attrs = {
                    id("firebaseui-auth-container")
                }) {}
                
                // Alternative: Manual auth forms if Firebase UI fails
                if (errorMessage != null) {
                    Div({
                        style {
                            marginTop(1.em)
                            paddingTop(1.em)
                            property("border-top", "1px solid ${ThemeColors.divider}")
                        }
                    }) {
                        P({
                            style {
                                color(ThemeColors.textMuted)
                                fontSize(0.9.em)
                                marginBottom(1.em)
                            }
                        }) { Text("Or try the mobile app for better experience:") }
                        
                        A(href = "https://play.google.com/store/apps/details?id=com.isaias.fyattravel", attrs = {
                            style {
                                display(DisplayStyle.Block)
                                textAlign("center")
                                backgroundColor(ThemeColors.primary)
                                color(ThemeColors.textOnGold)
                                padding(0.75.em, 1.em)
                                borderRadius(0.75.em)
                                textDecoration("none")
                                fontWeight("600")
                            }
                            attr("target", "_blank")
                            attr("rel", "noopener noreferrer")
                        }) {
                            Text("Download Android App")
                        }
                    }
                }
            }
        }
        
        // Already logged in message
        if (authState.isAuthenticated) {
            Div({
                style {
                    backgroundColor(ThemeColors.successBg)
                    color(ThemeColors.success)
                    padding(1.em)
                    borderRadius(0.75.em)
                    textAlign("center")
                }
            }) {
                P({ style { fontWeight("600") } }) { Text("You are already signed in!") }
                authState.user?.displayName?.let { name ->
                    P({ style { marginTop(0.5.em) } }) { Text("Welcome, $name") }
                }
                
                Button({
                    style {
                        marginTop(1.em)
                        backgroundColor(ThemeColors.success)
                        color(ThemeColors.textOnGold)
                        padding(0.75.em, 1.5.em)
                        borderRadius(0.75.em)
                        border(0.px)
                        cursor("pointer")
                        fontWeight("600")
                    }
                    onClick { onAuthSuccess() }
                }) {
                    Text("Continue to App")
                }
            }
        }
        
        // Footer
        Div({
            style {
                marginTop(2.em)
                textAlign("center")
                fontSize(0.85.em)
                color(ThemeColors.textSecondary)
            }
        }) {
            Text("By signing in, you agree to our Terms of Service and Privacy Policy.")
            
            Div({ style { marginTop(1.em) } }) {
                A(href = "#/contact", attrs = {
                    style {
                        color(ThemeColors.primary)
                        textDecoration("none")
                        fontWeight("600")
                    }
                }) { Text("Need help? Contact us") }
            }
        }
    }
}

/**
 * User Profile Page
 * Shows user info and allows logout
 */
@Composable
fun UserProfilePage(
    onNavigateHome: () -> Unit = { window.location.hash = "#/" }
) {
    val authState = rememberAuthState()
    var showLogoutConfirm by remember { mutableStateOf(false) }
    
    // Redirect to auth if not logged in
    LaunchedEffect(authState.isAuthenticated, authState.isLoading) {
        if (!authState.isLoading && !authState.isAuthenticated) {
            window.location.hash = "#/auth"
        }
    }
    
    if (authState.isLoading) {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                padding(4.em)
            }
        }) {
            Div({
                style {
                    width(40.px)
                    height(40.px)
                    borderRadius(50.percent)
                    property("border", "4px solid ${ThemeColors.divider}")
                    property("border-top-color", "${ThemeColors.primary}")
                    property("animation", "spin 1s linear infinite")
                }
            }) {}
        }
        return
    }
    
    val user = authState.user ?: return
    
    Div({
        style {
            maxWidth(600.px)
            property("margin", "0 auto")
            padding(2.em)
        }
    }) {
        // Header
        H1({
            style {
                fontSize(2.em)
                fontWeight("800")
                color(ThemeColors.textPrimary)
                marginBottom(0.5.em)
            }
        }) { Text(WebStrings.t(WebStringKey.MY_PROFILE)) }
        
        // Profile Card
        Div({
            style {
                backgroundColor(ThemeColors.surface)
                padding(2.em)
                borderRadius(1.em)
                property("box-shadow", "0 4px 12px rgba(0, 0, 0, 0.08)")
                marginBottom(2.em)
            }
        }) {
            // Avatar and Name
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(1.em)
                    marginBottom(1.5.em)
                    paddingBottom(1.5.em)
                    property("border-bottom", "1px solid ${ThemeColors.divider}")
                }
            }) {
                // Avatar
                if (user.photoURL != null) {
                    Img(src = user.photoURL, attrs = {
                        style {
                            width(80.px)
                            height(80.px)
                            borderRadius(50.percent)
                            property("object-fit", "cover")
                        }
                    })
                } else {
                    Div({
                        style {
                            width(80.px)
                            height(80.px)
                            borderRadius(50.percent)
                            backgroundColor(ThemeColors.primary)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            color(ThemeColors.textOnGold)
                            fontSize(2.em)
                            fontWeight("700")
                        }
                    }) {
                        Text(user.displayName?.firstOrNull()?.uppercase() ?: user.email?.firstOrNull()?.uppercase() ?: "?")
                    }
                }
                
                Div {
                    H2({
                        style {
                            fontSize(1.5.em)
                            fontWeight("700")
                            color(ThemeColors.textPrimary)
                            margin(0.px)
                        }
                    }) { Text(user.displayName ?: WebStrings.t(WebStringKey.USER_LABEL)) }
                    
                    user.email?.let { email ->
                        P({
                            style {
                                color(ThemeColors.textMuted)
                                marginTop(0.25.em)
                            }
                        }) { Text(email) }
                    }
                    
                    if (!user.isEmailVerified) {
                        Div({
                            style {
                                property("display", "inline-flex")
                                alignItems(AlignItems.Center)
                                gap(0.5.em)
                                marginTop(0.5.em)
                                padding(0.25.em, 0.75.em)
                                backgroundColor(ThemeColors.primaryMuted)
                                color(ThemeColors.warning)
                                borderRadius(0.5.em)
                                fontSize(0.85.em)
                                fontWeight("600")
                            }
                        }) {
                            Text(WebStrings.t(WebStringKey.EMAIL_NOT_VERIFIED))
                        }
                    }
                }
            }
            
            // User Info Grid
            Div({
                style {
                    display(DisplayStyle.Grid)
                    gridTemplateColumns("1fr 1fr")
                    gap(1.em)
                    property("@media (max-width: 640px)", "{ grid-template-columns: 1fr; }")
                }
            }) {
                InfoItem(WebStrings.t(WebStringKey.USER_ID), user.uid.take(8) + "...")
                InfoItem(WebStrings.t(WebStringKey.PHONE_NUMBER), user.phoneNumber ?: WebStrings.t(WebStringKey.NOT_PROVIDED))
                InfoItem(
                    WebStrings.t(WebStringKey.EMAIL_VERIFIED),
                    if (user.isEmailVerified) WebStrings.t(WebStringKey.YES_TEXT) else WebStrings.t(WebStringKey.NO_TEXT)
                )
                InfoItem(
                    WebStrings.t(WebStringKey.ACCOUNT_TYPE),
                    if (user.email != null) WebStrings.t(WebStringKey.ACCOUNT_TYPE_EMAIL) else WebStrings.t(WebStringKey.ACCOUNT_TYPE_PHONE)
                )
            }
        }
        
        // Actions Card
        Div({
            style {
                backgroundColor(ThemeColors.surface)
                padding(1.5.em)
                borderRadius(1.em)
                property("box-shadow", "0 4px 12px rgba(0, 0, 0, 0.08)")
            }
        }) {
            H3({
                style {
                    fontSize(1.1.em)
                    fontWeight("700")
                    color(ThemeColors.textPrimary)
                    marginBottom(1.em)
                }
            }) { Text(WebStrings.t(WebStringKey.ACCOUNT_ACTIONS)) }
            
            // Logout Button
            Button({
                style {
                    width(100.percent)
                    backgroundColor(ThemeColors.error)
                    color(ThemeColors.textOnGold)
                    padding(0.75.em)
                    borderRadius(0.75.em)
                    border(0.px)
                    cursor("pointer")
                    fontWeight("600")
                    fontSize(1.em)
                }
                onClick { showLogoutConfirm = true }
            }) {
                Text(WebStrings.t(WebStringKey.SIGN_OUT))
            }
        }
        
        // Logout Confirmation Modal
        if (showLogoutConfirm) {
            Div({
                style {
                    position(Position.Fixed)
                    top(0.px)
                    left(0.px)
                    right(0.px)
                    bottom(0.px)
                    backgroundColor(ThemeColors.shadow)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    property("z-index", "1000")
                }
            }) {
                Div({
                    style {
                        backgroundColor(ThemeColors.surface)
                        padding(2.em)
                        borderRadius(1.em)
                        maxWidth(400.px)
                        width(90.percent)
                    }
                }) {
                    H3({
                        style {
                            fontSize(1.25.em)
                            fontWeight("700")
                            color(ThemeColors.textPrimary)
                            marginBottom(0.5.em)
                        }
                    }) { Text(WebStrings.t(WebStringKey.SIGN_OUT_TITLE)) }
                    
                    P({
                        style {
                                color(ThemeColors.textMuted)
                            marginBottom(1.5.em)
                        }
                    }) { Text(WebStrings.t(WebStringKey.SIGN_OUT_CONFIRMATION)) }
                    
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(1.em)
                        }
                    }) {
                        Button({
                            style {
                                flex(1)
                    backgroundColor(ThemeColors.skeleton)
                    color(ThemeColors.textMuted)
                                padding(0.75.em)
                                borderRadius(0.75.em)
                                border(0.px)
                                cursor("pointer")
                                fontWeight("600")
                            }
                            onClick { showLogoutConfirm = false }
                        }) {
                            Text(WebStrings.t(WebStringKey.CANCEL))
                        }
                        
                        Button({
                            style {
                                flex(1)
                                backgroundColor(ThemeColors.error)
                                color(ThemeColors.textOnGold)
                                padding(0.75.em)
                                borderRadius(0.75.em)
                                border(0.px)
                                cursor("pointer")
                                fontWeight("600")
                            }
                            onClick {
                                AuthManager.signOut {
                                    showLogoutConfirm = false
                                    window.location.hash = "#/"
                                }
                            }
                        }) {
                            Text(WebStrings.t(WebStringKey.SIGN_OUT))
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun InfoItem(label: String, value: String) {
    Div {
        Div({
            style {
                fontSize(0.8.em)
                color(ThemeColors.textSecondary)
                marginBottom(0.25.em)
                fontWeight("600")
            }
        }) { Text(label) }
        Div({
            style {
                color(ThemeColors.textMuted)
                fontWeight("500")
            }
        }) { Text(value) }
    }
}
